import React from 'react';
import './TermsPage.css';

const EFFECTIVE_DATE = 'February 8, 2026';
const LAST_UPDATED = 'February 11, 2026';

const sections = [
  { id: 'service-description', num: '1', title: 'Service Description & Acceptance' },
  { id: 'account-registration', num: '2', title: 'Account Registration & Security' },
  { id: 'subscriptions', num: '3', title: 'Subscription Plans, Billing & Cancellation' },
  { id: 'free-trial', num: '4', title: 'Free Trial Terms' },
  { id: 'acceptable-use', num: '5', title: 'Acceptable Use Policy' },
  { id: 'bannable-offenses', num: '6', title: 'Bannable Offenses' },
  { id: 'user-content', num: '7', title: 'User-Generated Content' },
  { id: 'intellectual-property', num: '8', title: 'Intellectual Property' },
  { id: 'privacy', num: '9', title: 'Privacy' },
  { id: 'third-party', num: '10', title: 'Third-Party Services' },
  { id: 'warranties', num: '11', title: 'Disclaimer of Warranties' },
  { id: 'liability', num: '12', title: 'Limitation of Liability' },
  { id: 'indemnification', num: '13', title: 'Indemnification' },
  { id: 'termination', num: '14', title: 'Account Termination & Suspension' },
  { id: 'data-retention', num: '15', title: 'Data Retention & Deletion' },
  { id: 'modifications', num: '16', title: 'Modifications to Terms' },
  { id: 'governing-law', num: '17', title: 'Governing Law & Dispute Resolution' },
  { id: 'cig-disclaimer', num: '18', title: 'CIG / RSI Disclaimer' },
  { id: 'contact', num: '19', title: 'Contact Information' },
];

const BackToTop = () => (
  <a href="#terms-top" className="terms-back-to-top">Back to top</a>
);

const TermsPage = () => (
  <div id="terms-top" className="page-container legal-page terms-page">
    {/* ── Header ── */}
    <header className="terms-header">
      <h1 className="terms-title">Terms of Service</h1>
      <p className="terms-meta"><strong>Effective Date:</strong> {EFFECTIVE_DATE}</p>
      <p className="terms-meta"><strong>Last Updated:</strong> {LAST_UPDATED}</p>
      <p className="terms-intro">
        Welcome to The Orbital Ledger. These Terms of Service ("Terms") constitute a legally binding
        agreement between you ("User," "you," or "your") and The Orbital Ledger ("TOL," "we," "us,"
        or "our"), governing your access to and use of the website located at{' '}
        <a href="https://theorbitalledger.com" className="terms-internal-link" target="_blank" rel="noopener noreferrer">
          theorbitalledger.com
        </a>{' '}
        and all related services (collectively, the "Service").
      </p>
      <p className="terms-intro">
        By using this Service, you also agree to our{' '}
        <a href="/community-rules" className="terms-internal-link">Community Rules</a>{' '}
        and{' '}
        <a href="/privacy" className="terms-internal-link">Privacy Policy</a>,
        which are incorporated into these Terms by reference.
      </p>
    </header>

    {/* ── Table of Contents ── */}
    <nav className="terms-toc">
      <div className="terms-toc-title">Table of Contents</div>
      <ol className="terms-toc-list">
        {sections.map((s) => (
          <li key={s.id}>
            <a href={`#${s.id}`} className="terms-toc-link">
              <span className="terms-toc-number">{s.num}.</span>
              {s.title}
            </a>
          </li>
        ))}
      </ol>
    </nav>

    {/* ═══════════════════════════════════════════════
        SECTION 1 — Service Description & Acceptance
    ═══════════════════════════════════════════════ */}
    <section id="service-description" className="terms-section">
      <h2 className="terms-section-heading">
        <span className="terms-section-number">1.</span> Service Description & Acceptance
      </h2>
      <p className="terms-text">
        The Orbital Ledger is a fan-made, community-driven Software-as-a-Service (SaaS) web application
        designed to enhance the Star Citizen gameplay experience. TOL provides the following features,
        among others:
      </p>
      <ul className="terms-list terms-list-disc">
        <li>Personal inventory tracking and management</li>
        <li>Trade route planning and optimization tools</li>
        <li>Real-time commodity price charts and data submissions</li>
        <li>Organization (org) management, cargo tracking, and analytics</li>
        <li>Player-to-player marketplace ("Bazaar") for in-game item listings</li>
        <li>Ship hangar management and comparison tools</li>
        <li>Community features including direct messaging, reviews, and leaderboards</li>
      </ul>
      <p className="terms-text">
        By creating an account, accessing, or using any part of the Service, you acknowledge that you
        have read, understood, and agree to be bound by these Terms. If you do not agree to these Terms,
        you must not use the Service.
      </p>
      <p className="terms-text">
        You must be at least 13 years of age to use this Service. If you are between 13 and 18 years
        of age (or the age of legal majority in your jurisdiction), you may only use the Service with
        the consent of a parent or legal guardian who agrees to be bound by these Terms.
      </p>
      <BackToTop />
    </section>

    {/* ═══════════════════════════════════════════════
        SECTION 2 — Account Registration & Security
    ═══════════════════════════════════════════════ */}
    <section id="account-registration" className="terms-section">
      <h2 className="terms-section-heading">
        <span className="terms-section-number">2.</span> Account Registration & Security
      </h2>
      <p className="terms-text">
        To access certain features of the Service, you must register for an account. When registering,
        you agree to:
      </p>
      <ol className="terms-list">
        <li>
          <span className="terms-bold">Accurate Information.</span> Provide accurate, current, and
          complete information during registration and keep your profile information up to date.
        </li>
        <li>
          <span className="terms-bold">Account Security.</span> Maintain the confidentiality of your
          login credentials. You are solely responsible for all activity that occurs under your account.
          You must immediately notify us at{' '}
          <a href="mailto:support@theorbitalledger.com" className="terms-internal-link">
            support@theorbitalledger.com
          </a>{' '}
          if you suspect unauthorized access.
        </li>
        <li>
          <span className="terms-bold">One Account Per Person.</span> Each individual may maintain only
          one account. Creating multiple accounts to circumvent bans, abuse free trials, or manipulate
          community features is prohibited.
        </li>
        <li>
          <span className="terms-bold">Authentication.</span> Account authentication is provided through
          Supabase Auth. We support email/password registration. You are responsible for maintaining the
          security of your authentication credentials.
        </li>
      </ol>
      <p className="terms-text">
        We reserve the right to refuse registration, suspend, or terminate accounts at our sole
        discretion if we believe a violation of these Terms has occurred.
      </p>
      <BackToTop />
    </section>

    {/* ═══════════════════════════════════════════════
        SECTION 3 — Subscription Plans, Billing & Cancellation
    ═══════════════════════════════════════════════ */}
    <section id="subscriptions" className="terms-section">
      <h2 className="terms-section-heading">
        <span className="terms-section-number">3.</span> Subscription Plans, Billing & Cancellation
      </h2>

      <h3 className="terms-card-title">3.1 Available Plans</h3>
      <p className="terms-text">
        The Service offers both free and paid subscription tiers. Paid plans provide access to premium
        features and are billed on a monthly recurring basis. Current subscription offerings include:
      </p>
      <table className="terms-pricing-table">
        <thead>
          <tr>
            <th>Plan</th>
            <th>Price</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Free Tier</td>
            <td>$0.00/mo</td>
            <td>Basic access to community features, limited inventory tracking</td>
          </tr>
          <tr>
            <td>Ledger Pro (Personal)</td>
            <td>$4.99/mo</td>
            <td>Unlimited inventory, goal tracking, advanced personal tools</td>
          </tr>
          <tr>
            <td>Org Command - Squadron</td>
            <td>$9.99/mo</td>
            <td>Org creation, cargo management, member seat controls (small orgs)</td>
          </tr>
          <tr>
            <td>Org Command - Fleet</td>
            <td>$24.99/mo</td>
            <td>Expanded org features, advanced analytics (medium orgs)</td>
          </tr>
          <tr>
            <td>Org Command - Armada</td>
            <td>$49.99/mo</td>
            <td>Full org suite, maximum member seats, priority support (large orgs)</td>
          </tr>
        </tbody>
      </table>
      <p className="terms-text terms-emphasis">
        Plan features and pricing are subject to change. We will provide at least 30 days' notice before
        any price increase takes effect on your existing subscription.
      </p>

      <h3 className="terms-card-title">3.2 Payment Processing</h3>
      <p className="terms-text">
        All payments are processed securely through <span className="terms-bold">Stripe, Inc.</span>{' '}
        ("Stripe"). By subscribing to a paid plan, you agree to Stripe's{' '}
        <a href="https://stripe.com/legal" className="terms-internal-link" target="_blank" rel="noopener noreferrer">
          Terms of Service
        </a>{' '}
        and{' '}
        <a href="https://stripe.com/privacy" className="terms-internal-link" target="_blank" rel="noopener noreferrer">
          Privacy Policy
        </a>.
        TOL does not store your full credit card number, CVV, or other sensitive payment card data on
        our servers. All payment card data is handled directly by Stripe in compliance with PCI-DSS
        standards.
      </p>

      <h3 className="terms-card-title">3.3 Billing Cycle</h3>
      <p className="terms-text">
        Subscriptions are billed on a monthly recurring cycle beginning on the date of your initial
        purchase (or at the end of any applicable free trial). Your payment method will be automatically
        charged at the start of each billing cycle unless you cancel before the renewal date.
      </p>

      <h3 className="terms-card-title">3.4 Cancellation</h3>
      <ul className="terms-list terms-list-disc">
        <li>
          You may cancel your subscription at any time through your account settings or by contacting
          us at{' '}
          <a href="mailto:support@theorbitalledger.com" className="terms-internal-link">
            support@theorbitalledger.com
          </a>.
        </li>
        <li>
          Upon cancellation, your paid features will remain active until the end of your current billing
          period. You will not be charged for subsequent billing cycles.
        </li>
        <li>
          After your paid period expires, your account will revert to the Free Tier. Your data will be
          retained but access to premium features will be restricted.
        </li>
      </ul>

      <h3 className="terms-card-title">3.5 Refunds</h3>
      <p className="terms-text">
        Due to the nature of digital services, all sales are generally final. However, we may issue
        refunds at our sole discretion in the following circumstances:
      </p>
      <ul className="terms-list terms-list-disc">
        <li>Billing errors or duplicate charges.</li>
        <li>Extended service outages that materially prevent use of paid features.</li>
        <li>Other exceptional circumstances as determined by TOL.</li>
      </ul>
      <p className="terms-text">
        <span className="terms-bold">Prorated Refunds.</span> Where a refund is granted, the refund
        amount will be <span className="terms-bold">prorated based on the unused portion</span> of
        the current billing period. The prorated amount is calculated as: (remaining days in billing
        period / total days in billing period) &times; subscription price. Days already used during
        the billing period are non-refundable. For example, if a refund is requested on day 20 of a
        30-day billing cycle at $4.99/month, the refund would be approximately $1.66 (10 remaining
        days / 30 total days &times; $4.99).
      </p>
      <p className="terms-text">
        <span className="terms-bold">Refund Window.</span> Refund requests must be submitted within
        14 days of the charge in question to{' '}
        <a href="mailto:support@theorbitalledger.com" className="terms-internal-link">
          support@theorbitalledger.com
        </a>.
        Refunds, where granted, will be processed through Stripe to the original payment method.
        Please allow 5-10 business days for the refund to appear on your statement.
      </p>
      <p className="terms-text">
        <span className="terms-bold">No Refund on Account Termination for Violations.</span> Users
        whose accounts are terminated due to violations of these Terms (see{' '}
        <a href="#bannable-offenses" className="terms-internal-link">Section 6</a>) are not entitled
        to a refund for any remaining subscription period.
      </p>

      <h3 className="terms-card-title">3.6 Failed Payments</h3>
      <p className="terms-text">
        If a recurring payment fails, we will attempt to retry the charge as permitted by Stripe. If
        payment cannot be collected after reasonable attempts, your subscription may be downgraded to
        the Free Tier. We will notify you via email before any downgrade occurs.
      </p>

      <h3 id="gift-subscriptions" className="terms-card-title">3.7 Gift Subscriptions</h3>
      <p className="terms-text">
        TOL allows users to purchase gift subscriptions for organizations. The following terms apply
        to all gift subscription purchases:
      </p>
      <ol className="terms-list">
        <li>
          <span className="terms-bold">Purchase & Delivery.</span> Gift subscriptions are one-time
          purchases that grant the recipient organization a specified duration (1-12 months) of a
          selected Org Command tier. The gift is delivered to the organization and must be applied
          by an authorized org leader.
        </li>
        <li>
          <span className="terms-bold">Application.</span> The receiving org leader may choose to
          apply the gift as either an extension of their current subscription tier or an upgrade to
          the gifted tier. Once applied, a gift subscription cannot be unapplied, transferred to a
          different organization, or converted to cash value.
        </li>
        <li>
          <span className="terms-bold">Refunds for Gifts.</span> Refund requests for gift
          subscriptions must be submitted by the <span className="terms-bold">original purchaser</span>.
          Refunds are issued exclusively to the original purchaser's payment method, not to the gift
          recipient. If a gift subscription is refunded:
          <ul className="terms-bullet-list">
            <li>The refund amount will be prorated based on unused time remaining, consistent with
              the prorated refund policy in Section 3.5.</li>
            <li>The recipient organization's subscription will be adjusted to remove the refunded
              gifted time.</li>
            <li>The gift recipient has no independent right to request a refund for a gift they
              received.</li>
          </ul>
        </li>
        <li>
          <span className="terms-bold">No Resale or Transfer.</span> Gift subscriptions may not be
          resold, traded, auctioned, or transferred for real-world or in-game value. Attempting to
          sell gift subscriptions constitutes a violation of these Terms and may result in account
          termination.
        </li>
        <li>
          <span className="terms-bold">Anonymous Gifts.</span> Purchasers may choose to send gifts
          anonymously. TOL will not disclose the identity of anonymous gift purchasers to recipients
          except as required by law or in response to valid refund requests.
        </li>
      </ol>
      <BackToTop />
    </section>

    {/* ═══════════════════════════════════════════════
        SECTION 4 — Free Trial Terms
    ═══════════════════════════════════════════════ */}
    <section id="free-trial" className="terms-section">
      <h2 className="terms-section-heading">
        <span className="terms-section-number">4.</span> Free Trial Terms
      </h2>
      <p className="terms-text">
        We may offer a <span className="terms-bold">14-day free trial</span> of paid features to
        eligible new users. The following terms apply to free trials:
      </p>
      <ol className="terms-list">
        <li>
          <span className="terms-bold">No Credit Card Required.</span> You are not required to provide
          payment information to begin a free trial.
        </li>
        <li>
          <span className="terms-bold">Trial Duration.</span> Free trials last 14 calendar days from
          the date of activation. At the end of the trial, access to premium features will be
          restricted unless you subscribe to a paid plan.
        </li>
        <li>
          <span className="terms-bold">One Trial Per User.</span> Free trials are limited to one per
          person and per account. Abuse of the trial system (including creating multiple accounts) is
          a violation of these Terms and may result in account termination.
        </li>
        <li>
          <span className="terms-bold">No Automatic Charges.</span> Your free trial will not
          automatically convert to a paid subscription. You must actively choose to subscribe.
        </li>
        <li>
          <span className="terms-bold">Trial Availability.</span> We reserve the right to modify,
          suspend, or discontinue free trial offers at any time without prior notice.
        </li>
      </ol>
      <BackToTop />
    </section>

    {/* ═══════════════════════════════════════════════
        SECTION 5 — Acceptable Use Policy
    ═══════════════════════════════════════════════ */}
    <section id="acceptable-use" className="terms-section">
      <h2 className="terms-section-heading">
        <span className="terms-section-number">5.</span> Acceptable Use Policy
      </h2>
      <p className="terms-text">
        You agree to use the Service only for its intended purposes and in compliance with all
        applicable laws. The following activities are strictly prohibited:
      </p>
      <ol className="terms-list" style={{ listStyleType: 'lower-alpha' }}>
        <li>
          <span className="terms-bold">Data Manipulation.</span> Submitting false, fraudulent, or
          intentionally misleading pricing data, item information, location data, or any other
          community-contributed content.
        </li>
        <li>
          <span className="terms-bold">Harassment & Abuse.</span> Engaging in harassment, bullying,
          hate speech, threats, doxxing, or any abusive behavior directed at other users.
        </li>
        <li>
          <span className="terms-bold">Spam & Advertising.</span> Posting unsolicited advertisements,
          promotional content, phishing links, or spam through any Service feature including
          marketplace listings, reviews, messages, or comments.
        </li>
        <li>
          <span className="terms-bold">System Abuse.</span> Attempting to exploit, hack, reverse
          engineer, decompile, or disassemble any part of the Service; deploying bots, scrapers,
          or automated tools to extract data without authorization; or conducting denial-of-service
          attacks.
        </li>
        <li>
          <span className="terms-bold">Impersonation.</span> Impersonating another user, moderator,
          administrator, or any representative of TOL, CIG, or RSI.
        </li>
        <li>
          <span className="terms-bold">Ban Evasion.</span> Attempting to circumvent account bans,
          suspensions, or other moderation actions by creating new accounts or using alternate
          identities.
        </li>
        <li>
          <span className="terms-bold">Marketplace Fraud.</span> Creating fraudulent marketplace
          listings, engaging in scam transactions, or manipulating reviews and trust ratings.
        </li>
        <li>
          <span className="terms-bold">Real-Money Trading (RMT).</span> Using any feature of the
          Service — including but not limited to the marketplace, direct messages, organization tools,
          or any listing or communication channel — to facilitate, solicit, advertise, or conduct
          real-money transactions (exchanging real currency, cryptocurrency, gift cards, or any
          real-world value for in-game items, currency, ships, or services) between players.
          Permitted transactions include in-game currency (aUEC) payments, direct in-game item-for-item
          trades (bartering), or any combination thereof — provided the entire exchange consists
          exclusively of in-game assets. RMT violations will result in immediate permanent account
          termination and may be reported to Cloud Imperium Games.
        </li>
        <li>
          <span className="terms-bold">Illegal Activity.</span> Using the Service for any activity
          that violates local, state, national, or international law.
        </li>
      </ol>
      <p className="terms-text">
        We reserve the right to investigate suspected violations and take appropriate action, including
        but not limited to content removal, account suspension, permanent bans, and referral to law
        enforcement where warranted.
      </p>
      <BackToTop />
    </section>

    {/* ═══════════════════════════════════════════════
        SECTION 6 — Bannable Offenses
    ═══════════════════════════════════════════════ */}
    <section id="bannable-offenses" className="terms-section">
      <h2 className="terms-section-heading">
        <span className="terms-section-number">6.</span> Bannable Offenses
      </h2>
      <p className="terms-text">
        The following actions will result in immediate disciplinary action, up to and including permanent
        account termination. This is not an exhaustive list; any behavior that violates the spirit of
        these Terms or our{' '}
        <a href="/community-rules" className="terms-internal-link">Community Rules</a>{' '}
        may result in enforcement action:
      </p>
      <div className="terms-card terms-card-warning">
        <div className="terms-card-title">Zero-Tolerance Violations</div>
        <ul className="terms-list terms-list-disc">
          <li><span className="terms-bold">Submitting false or fraudulent pricing data</span></li>
          <li><span className="terms-bold">Intentionally adding misleading or troll content</span></li>
          <li><span className="terms-bold">Spamming users with advertisements or irrelevant content</span></li>
          <li><span className="terms-bold">Harassment, hate speech, or abusive behavior</span></li>
          <li><span className="terms-bold">Attempting to evade bans or circumvent moderation</span></li>
          <li><span className="terms-bold">Marketplace fraud, scam listings, or trust rating manipulation</span></li>
          <li><span className="terms-bold">Real-money trading (RMT) — exchanging real currency for in-game items, currency, or services</span></li>
          <li><span className="terms-bold">Exploiting bugs or vulnerabilities instead of reporting them</span></li>
          <li><span className="terms-bold">Abuse of the support ticket system, including submitting troll tickets, spam, false reports, or using the system for harassment</span></li>
          <li><span className="terms-bold">Any other conduct that violates our community guidelines or applicable laws</span></li>
        </ul>
      </div>
      <p className="terms-text">
        Enforcement actions are applied at the discretion of TOL moderators and administrators. Actions
        may include warnings, temporary suspensions, permanent bans, content removal, and restriction
        of account deletion rights (see{' '}
        <a href="#data-retention" className="terms-internal-link">Section 15</a>).
      </p>
      <p className="terms-text terms-emphasis">
        Banned users may have their account deletion rights restricted as described in our{' '}
        <a href="/privacy" className="terms-internal-link">Privacy Policy</a>.
      </p>
      <BackToTop />
    </section>

    {/* ═══════════════════════════════════════════════
        SECTION 7 — User-Generated Content
    ═══════════════════════════════════════════════ */}
    <section id="user-content" className="terms-section">
      <h2 className="terms-section-heading">
        <span className="terms-section-number">7.</span> User-Generated Content
      </h2>
      <p className="terms-text">
        The Service allows users to submit, post, and share various types of content, including but not
        limited to: commodity price submissions, marketplace listings, item reviews, organization data,
        trade route configurations, inventory entries, screenshots, and messages ("User Content").
      </p>

      <h3 className="terms-card-title">7.1 Ownership</h3>
      <p className="terms-text">
        You retain ownership of your User Content. By submitting User Content to the Service, you do
        not transfer ownership to TOL.
      </p>

      <h3 className="terms-card-title">7.2 License Grant</h3>
      <p className="terms-text">
        By submitting User Content, you grant TOL a non-exclusive, worldwide, royalty-free, sublicensable,
        and transferable license to use, reproduce, modify, display, distribute, and create derivative
        works from your User Content solely for the purpose of operating, improving, and promoting the
        Service. This license continues even if you stop using the Service, but only with respect to
        User Content that has been shared with or contributed to community features (such as price data,
        public marketplace listings, and reviews).
      </p>
      <p className="terms-text">
        For private data (such as personal inventory, private org data, and direct messages), this
        license terminates when you delete the content or your account, subject to reasonable backup
        retention periods and legal obligations.
      </p>

      <h3 className="terms-card-title">7.3 Community-Contributed Data</h3>
      <p className="terms-text">
        Certain submissions — including commodity price data, location information, and item details —
        contribute to community-wide databases that benefit all users. Once submitted to these shared
        databases, such data becomes part of the collective community dataset. While you may delete
        your account, previously contributed community data may be retained in anonymized form to
        preserve the integrity of the dataset.
      </p>

      <h3 className="terms-card-title">7.4 Content Responsibility</h3>
      <p className="terms-text">
        You are solely responsible for your User Content. You represent and warrant that:
      </p>
      <ul className="terms-list terms-list-disc">
        <li>You have the right to submit the content and grant the license described above.</li>
        <li>Your content does not infringe upon the intellectual property, privacy, or other rights of any third party.</li>
        <li>Your content complies with these Terms, our Community Rules, and all applicable laws.</li>
        <li>Your content is accurate and not intentionally misleading (particularly for price submissions and item data).</li>
      </ul>

      <h3 className="terms-card-title">7.5 Content Moderation</h3>
      <p className="terms-text">
        We reserve the right, but have no obligation, to monitor, review, edit, or remove any User
        Content at our sole discretion, for any reason, including but not limited to violations of these
        Terms. We are not liable for any User Content posted by users.
      </p>

      <h3 className="terms-card-title">7.6 Feedback, Suggestions & Feature Requests</h3>
      <p className="terms-text">
        By submitting any feedback, suggestions, feature requests, ideas, concepts, or proposals
        ("Feedback") to TOL through the Service — including but not limited to the support ticket
        system, direct messages, community forums, or any other communication channel — you agree
        to the following:
      </p>
      <ol className="terms-list">
        <li>
          <span className="terms-bold">Assignment of Rights.</span> You hereby irrevocably assign to
          TOL all right, title, and interest in and to such Feedback, including all intellectual
          property rights therein. TOL shall own such Feedback and shall be entitled to the
          unrestricted use and dissemination of the Feedback for any purpose, commercial or otherwise,
          without acknowledgment, attribution, or compensation to you.
        </li>
        <li>
          <span className="terms-bold">No Obligation.</span> TOL is under no obligation to implement,
          review, consider, or respond to any Feedback. Submission of Feedback does not create any
          confidential relationship, fiduciary duty, or implied contract between you and TOL.
        </li>
        <li>
          <span className="terms-bold">No Compensation.</span> You acknowledge and agree that you will
          not receive any compensation, credit, or royalties for any Feedback, regardless of whether
          TOL uses, modifies, or incorporates the Feedback into the Service or any other product.
        </li>
        <li>
          <span className="terms-bold">Waiver of Claims.</span> You waive any and all claims,
          including but not limited to claims of ownership, compensation, unjust enrichment, or
          misappropriation, that may arise from TOL's use of your Feedback. This waiver applies
          whether or not the Feedback is used in its original form or in a modified version.
        </li>
        <li>
          <span className="terms-bold">Representation.</span> You represent and warrant that your
          Feedback does not infringe upon the intellectual property rights, privacy rights, or any
          other rights of any third party, and that you have the full authority to grant the rights
          described in this section.
        </li>
      </ol>
      <p className="terms-text terms-emphasis">
        If you do not wish to grant these rights, do not submit Feedback through the Service.
      </p>
      <BackToTop />
    </section>

    {/* ═══════════════════════════════════════════════
        SECTION 8 — Intellectual Property
    ═══════════════════════════════════════════════ */}
    <section id="intellectual-property" className="terms-section">
      <h2 className="terms-section-heading">
        <span className="terms-section-number">8.</span> Intellectual Property
      </h2>

      <h3 className="terms-card-title">8.1 Star Citizen & CIG Intellectual Property</h3>
      <p className="terms-text">
        Star Citizen, Squadron 42, Roberts Space Industries, Cloud Imperium Games, and all related
        names, logos, images, ships, locations, items, and game content are the intellectual property
        of Cloud Imperium Games Corporation and/or its subsidiaries ("CIG"). TOL uses Star Citizen
        game assets, names, and data under CIG's Fan Site Kit guidelines and does not claim ownership
        of any CIG intellectual property.
      </p>

      <h3 className="terms-card-title">8.2 TOL Intellectual Property</h3>
      <p className="terms-text">
        The Orbital Ledger name, logo, website design, source code, algorithms, database structures,
        user interface elements, original graphics, and all other original content created by TOL are
        the exclusive property of The Orbital Ledger and its creators. You may not copy, reproduce,
        distribute, reverse engineer, or create derivative works from TOL's proprietary materials
        without our express written consent.
      </p>

      <h3 className="terms-card-title">8.3 User Data Ownership</h3>
      <p className="terms-text">
        You own the personal data you provide to the Service, including your personal inventory
        records, private trade routes, and personal account information. See{' '}
        <a href="#user-content" className="terms-internal-link">Section 7</a> for details on
        the license you grant when contributing to community features.
      </p>

      <h3 className="terms-card-title">8.4 DMCA & Copyright Complaints</h3>
      <p className="terms-text">
        If you believe that content on the Service infringes your copyright, please contact us at{' '}
        <a href="mailto:support@theorbitalledger.com" className="terms-internal-link">
          support@theorbitalledger.com
        </a>{' '}
        with a detailed description of the alleged infringement, identification of the copyrighted work,
        and your contact information. We will investigate and respond in accordance with applicable law.
      </p>
      <BackToTop />
    </section>

    {/* ═══════════════════════════════════════════════
        SECTION 9 — Privacy
    ═══════════════════════════════════════════════ */}
    <section id="privacy" className="terms-section">
      <h2 className="terms-section-heading">
        <span className="terms-section-number">9.</span> Privacy
      </h2>
      <p className="terms-text">
        Your privacy is important to us. Our collection, use, and disclosure of personal information is
        governed by our{' '}
        <a href="/privacy" className="terms-internal-link">Privacy Policy</a>,
        which is incorporated into these Terms by reference. By using the Service, you consent to the
        data practices described in the Privacy Policy.
      </p>
      <p className="terms-text">Key privacy highlights include:</p>
      <ul className="terms-list terms-list-disc">
        <li>We collect only information necessary to provide and improve the Service.</li>
        <li>We do not sell your personal data to third parties.</li>
        <li>Payment information is handled exclusively by Stripe and is never stored on our servers.</li>
        <li>You may request access to, correction of, or deletion of your personal data at any time.</li>
        <li>We comply with applicable data protection regulations, including GDPR and CCPA.</li>
      </ul>
      <p className="terms-text">
        For full details on our data practices, please review our{' '}
        <a href="/privacy" className="terms-internal-link">Privacy Policy</a>.
      </p>
      <BackToTop />
    </section>

    {/* ═══════════════════════════════════════════════
        SECTION 10 — Third-Party Services
    ═══════════════════════════════════════════════ */}
    <section id="third-party" className="terms-section">
      <h2 className="terms-section-heading">
        <span className="terms-section-number">10.</span> Third-Party Services
      </h2>
      <p className="terms-text">
        The Service relies on the following third-party service providers to deliver its functionality.
        By using the Service, you acknowledge and accept the terms of these providers:
      </p>
      <div className="terms-card terms-card-info">
        <table className="terms-pricing-table">
          <thead>
            <tr>
              <th>Provider</th>
              <th>Purpose</th>
              <th>Their Terms</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><span className="terms-bold">Stripe, Inc.</span></td>
              <td>Payment processing, subscription billing</td>
              <td>
                <a href="https://stripe.com/legal" className="terms-internal-link" target="_blank" rel="noopener noreferrer">
                  stripe.com/legal
                </a>
              </td>
            </tr>
            <tr>
              <td><span className="terms-bold">Supabase, Inc.</span></td>
              <td>Database hosting, user authentication</td>
              <td>
                <a href="https://supabase.com/terms" className="terms-internal-link" target="_blank" rel="noopener noreferrer">
                  supabase.com/terms
                </a>
              </td>
            </tr>
            <tr>
              <td><span className="terms-bold">Vercel, Inc.</span></td>
              <td>Website hosting and deployment</td>
              <td>
                <a href="https://vercel.com/legal/terms" className="terms-internal-link" target="_blank" rel="noopener noreferrer">
                  vercel.com/legal/terms
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="terms-text">
        TOL is not responsible for the practices, terms, or policies of third-party service providers.
        We encourage you to review the terms and privacy policies of each provider. In the event of a
        conflict between these Terms and the terms of a third-party provider with respect to that
        provider's services, the third-party provider's terms shall govern that specific relationship.
      </p>
      <p className="terms-text">
        The Service may contain links to third-party websites or services that are not owned or
        controlled by TOL. We have no control over, and assume no responsibility for, the content,
        privacy policies, or practices of any third-party sites or services.
      </p>
      <BackToTop />
    </section>

    {/* ═══════════════════════════════════════════════
        SECTION 11 — Disclaimer of Warranties
    ═══════════════════════════════════════════════ */}
    <section id="warranties" className="terms-section">
      <h2 className="terms-section-heading">
        <span className="terms-section-number">11.</span> Disclaimer of Warranties
      </h2>
      <div className="terms-card terms-card-warning">
        <p className="terms-text">
          <span className="terms-bold">
            THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS, WITHOUT WARRANTIES OF ANY
            KIND, EITHER EXPRESS OR IMPLIED.
          </span>
        </p>
        <p className="terms-text">
          TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, TOL DISCLAIMS ALL WARRANTIES, EXPRESS OR
          IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
          PARTICULAR PURPOSE, NON-INFRINGEMENT, AND ANY WARRANTIES ARISING OUT OF COURSE OF DEALING
          OR USAGE OF TRADE.
        </p>
      </div>
      <p className="terms-text">Without limiting the foregoing, TOL does not warrant that:</p>
      <ul className="terms-list terms-list-disc">
        <li>The Service will be uninterrupted, timely, secure, or error-free.</li>
        <li>The data, pricing information, or other content available through the Service will be accurate, complete, or current.</li>
        <li>Any defects in the Service will be corrected.</li>
        <li>The Service will meet your specific requirements or expectations.</li>
        <li>The Service will be compatible with future updates to Star Citizen or any other game.</li>
      </ul>
      <p className="terms-text">
        Community-submitted data (including commodity prices, item stats, and location information) is
        provided by users and has not been independently verified by TOL. You use such data at your own
        risk. TOL is not responsible for any in-game losses, poor trade decisions, or other consequences
        resulting from reliance on community-submitted data.
      </p>
      <BackToTop />
    </section>

    {/* ═══════════════════════════════════════════════
        SECTION 12 — Limitation of Liability
    ═══════════════════════════════════════════════ */}
    <section id="liability" className="terms-section">
      <h2 className="terms-section-heading">
        <span className="terms-section-number">12.</span> Limitation of Liability
      </h2>
      <div className="terms-card terms-card-warning">
        <p className="terms-text">
          <span className="terms-bold">
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL TOL, ITS CREATORS,
            CONTRIBUTORS, MODERATORS, OR AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
            CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA,
            USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, ARISING OUT OF OR RELATED TO YOUR USE OF OR
            INABILITY TO USE THE SERVICE.
          </span>
        </p>
      </div>
      <p className="terms-text">This limitation applies regardless of whether the damages are based on:</p>
      <ul className="terms-list terms-list-disc">
        <li>Breach of contract, warranty, or condition</li>
        <li>Tort (including negligence)</li>
        <li>Strict liability</li>
        <li>Any other legal theory</li>
      </ul>
      <p className="terms-text">
        <span className="terms-bold">
          IN ANY CASE, TOL'S TOTAL AGGREGATE LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATED
          TO THE SERVICE SHALL NOT EXCEED THE GREATER OF: (A) THE TOTAL AMOUNT YOU PAID TO TOL IN
          SUBSCRIPTION FEES DURING THE TWELVE (12) MONTHS IMMEDIATELY PRECEDING THE EVENT GIVING RISE
          TO THE CLAIM, OR (B) FIFTY U.S. DOLLARS ($50.00).
        </span>
      </p>
      <p className="terms-text">
        Some jurisdictions do not allow the exclusion or limitation of liability for consequential or
        incidental damages, so the above limitations may not apply to you. In such jurisdictions, our
        liability is limited to the greatest extent permitted by law.
      </p>
      <BackToTop />
    </section>

    {/* ═══════════════════════════════════════════════
        SECTION 13 — Indemnification
    ═══════════════════════════════════════════════ */}
    <section id="indemnification" className="terms-section">
      <h2 className="terms-section-heading">
        <span className="terms-section-number">13.</span> Indemnification
      </h2>
      <p className="terms-text">
        You agree to indemnify, defend, and hold harmless TOL, its creators, contributors, moderators,
        and affiliates from and against any and all claims, damages, obligations, losses, liabilities,
        costs, and expenses (including reasonable attorney's fees) arising from:
      </p>
      <ol className="terms-list" style={{ listStyleType: 'lower-alpha' }}>
        <li>Your use of or access to the Service.</li>
        <li>Your violation of these Terms, the Community Rules, or any applicable law or regulation.</li>
        <li>Your User Content, including any claims that your content infringes or violates the rights of a third party.</li>
        <li>Your interaction with other users, including any disputes arising from marketplace transactions.</li>
        <li>Any misrepresentation by you.</li>
      </ol>
      <p className="terms-text">
        This indemnification obligation will survive the termination of your account and these Terms.
      </p>
      <BackToTop />
    </section>

    {/* ═══════════════════════════════════════════════
        SECTION 14 — Account Termination & Suspension
    ═══════════════════════════════════════════════ */}
    <section id="termination" className="terms-section">
      <h2 className="terms-section-heading">
        <span className="terms-section-number">14.</span> Account Termination & Suspension
      </h2>

      <h3 className="terms-card-title">14.1 Termination by You</h3>
      <p className="terms-text">
        You may terminate your account at any time by using the account deletion feature in your account
        settings ("Danger Zone"). Account deletion requires password confirmation. Upon deletion, all
        your personal data will be permanently removed, subject to the exceptions described in{' '}
        <a href="#data-retention" className="terms-internal-link">Section 15</a>.
      </p>
      <p className="terms-text">
        If you have an active paid subscription, cancelling your subscription before deleting your
        account is recommended. Deleting your account does not automatically entitle you to a refund
        for any remaining subscription period.
      </p>

      <h3 className="terms-card-title">14.2 Termination or Suspension by TOL</h3>
      <p className="terms-text">
        We may suspend or terminate your account, at our sole discretion, with or without notice, for
        any reason, including but not limited to:
      </p>
      <ul className="terms-list terms-list-disc">
        <li>Violation of these Terms or the Community Rules.</li>
        <li>Engaging in any bannable offense (see <a href="#bannable-offenses" className="terms-internal-link">Section 6</a>).</li>
        <li>Extended periods of inactivity (accounts inactive for more than 24 months may be flagged for review).</li>
        <li>Requests from law enforcement or government agencies.</li>
        <li>Discontinuation or material modification of the Service.</li>
        <li>Unexpected technical or security issues.</li>
      </ul>

      <h3 className="terms-card-title">14.3 Effect of Termination</h3>
      <p className="terms-text">Upon termination of your account:</p>
      <ul className="terms-list terms-list-disc">
        <li>Your right to access and use the Service will immediately cease.</li>
        <li>Any active subscriptions will be cancelled (no further charges will occur).</li>
        <li>Your private data will be scheduled for deletion in accordance with our data retention policies.</li>
        <li>Community-contributed data (price submissions, reviews, etc.) may be retained in anonymized form.</li>
        <li>Provisions of these Terms that by their nature should survive termination will remain in effect, including but not limited to Sections 7, 8, 11, 12, 13, 15, and 17.</li>
      </ul>
      <BackToTop />
    </section>

    {/* ═══════════════════════════════════════════════
        SECTION 15 — Data Retention & Deletion
    ═══════════════════════════════════════════════ */}
    <section id="data-retention" className="terms-section">
      <h2 className="terms-section-heading">
        <span className="terms-section-number">15.</span> Data Retention & Deletion
      </h2>
      <p className="terms-text">
        We are committed to handling your data responsibly and in compliance with applicable data
        protection laws, including the General Data Protection Regulation (GDPR) and the California
        Consumer Privacy Act (CCPA).
      </p>

      <div className="terms-card terms-card-info">
        <div className="terms-card-title">Account Deletion & Data Retention Policy</div>
        <ol className="terms-list">
          <li>
            <span className="terms-bold">Account Deletion:</span> Users may request to delete their
            account at any time through the application's "Danger Zone" settings. Account deletion
            requires password confirmation and will permanently remove all user data.
          </li>
          <li>
            <span className="terms-bold">Ban Protection:</span> For community safety and legal
            compliance, users who have been banned for violating our Terms of Service may be temporarily
            or permanently restricted from deleting their accounts. This restriction is necessary to:
            <ul className="terms-bullet-list">
              <li>Prevent immediate ban evasion</li>
              <li>Preserve evidence for legal proceedings if required</li>
              <li>Maintain the integrity of our moderation system</li>
            </ul>
          </li>
          <li>
            <span className="terms-bold">Data Minimization:</span> Banned users may request
            anonymization of personal data while maintaining the account structure for moderation
            purposes. Upon such requests, we will minimize personal data in accordance with applicable
            privacy laws.
          </li>
          <li>
            <span className="terms-bold">Legal Requests:</span> In all cases, we will comply with
            valid legal requests for data deletion or access as required by applicable laws, including
            GDPR, CCPA, and other privacy regulations.
          </li>
        </ol>
      </div>

      <h3 className="terms-card-title">15.1 Your Data Rights</h3>
      <p className="terms-text">
        Depending on your jurisdiction, you may have the following rights regarding your personal data:
      </p>
      <ul className="terms-list terms-list-disc">
        <li><span className="terms-bold">Right of Access:</span> Request a copy of the personal data we hold about you.</li>
        <li><span className="terms-bold">Right to Rectification:</span> Request correction of inaccurate personal data.</li>
        <li><span className="terms-bold">Right to Erasure:</span> Request deletion of your personal data, subject to legal retention requirements.</li>
        <li><span className="terms-bold">Right to Restrict Processing:</span> Request limitation of how we process your data.</li>
        <li><span className="terms-bold">Right to Data Portability:</span> Request your data in a portable, machine-readable format.</li>
        <li><span className="terms-bold">Right to Object:</span> Object to certain processing of your personal data.</li>
        <li><span className="terms-bold">Right to Withdraw Consent:</span> Withdraw consent at any time where processing is based on consent.</li>
      </ul>
      <p className="terms-text">
        To exercise any of these rights, please contact us at{' '}
        <a href="mailto:support@theorbitalledger.com" className="terms-internal-link">
          support@theorbitalledger.com
        </a>.
        We will respond to your request within 30 days (or as required by applicable law).
      </p>

      <h3 className="terms-card-title">15.2 Data Retention Periods</h3>
      <ul className="terms-list terms-list-disc">
        <li><span className="terms-bold">Account data:</span> Retained while your account is active; deleted upon account deletion (subject to ban protections).</li>
        <li><span className="terms-bold">Payment records:</span> Retained for up to 7 years as required by financial record-keeping laws and Stripe's data retention policies.</li>
        <li><span className="terms-bold">Community-contributed data:</span> Price submissions, reviews, and other community data may be retained in anonymized form indefinitely.</li>
        <li><span className="terms-bold">Moderation records:</span> Logs of moderation actions (bans, warnings) are retained for up to 3 years for community safety purposes.</li>
        <li><span className="terms-bold">Server logs:</span> Technical logs are retained for up to 90 days for debugging and security purposes.</li>
      </ul>

      <h3 className="terms-card-title">15.3 CCPA-Specific Disclosures</h3>
      <p className="terms-text">
        If you are a California resident, you have additional rights under the CCPA, including the right
        to know what personal information we collect, the right to request deletion of your personal
        information, and the right to opt out of the sale of your personal information. We do not sell
        personal information. For CCPA-related requests, contact us at{' '}
        <a href="mailto:support@theorbitalledger.com" className="terms-internal-link">
          support@theorbitalledger.com
        </a>.
      </p>
      <BackToTop />
    </section>

    {/* ═══════════════════════════════════════════════
        SECTION 16 — Modifications to Terms
    ═══════════════════════════════════════════════ */}
    <section id="modifications" className="terms-section">
      <h2 className="terms-section-heading">
        <span className="terms-section-number">16.</span> Modifications to Terms
      </h2>
      <p className="terms-text">
        We reserve the right to modify, amend, or update these Terms at any time. When we make changes:
      </p>
      <ol className="terms-list">
        <li>
          <span className="terms-bold">Notice.</span> We will update the "Last Updated" date at the
          top of this page and, for material changes, provide notice through the Service (such as a
          banner notification, email, or in-app alert) at least 14 days before the changes take effect.
        </li>
        <li>
          <span className="terms-bold">Review Period.</span> You will have the opportunity to review
          updated Terms before they take effect. Continued use of the Service after the effective date
          constitutes acceptance of the revised Terms.
        </li>
        <li>
          <span className="terms-bold">Disagreement.</span> If you do not agree with the updated Terms,
          you must stop using the Service and may delete your account. Your continued use after the
          effective date of any revision means you accept and agree to the changes.
        </li>
      </ol>
      <p className="terms-text">
        We encourage you to periodically review these Terms to stay informed of any updates.
      </p>
      <BackToTop />
    </section>

    {/* ═══════════════════════════════════════════════
        SECTION 17 — Governing Law & Dispute Resolution
    ═══════════════════════════════════════════════ */}
    <section id="governing-law" className="terms-section">
      <h2 className="terms-section-heading">
        <span className="terms-section-number">17.</span> Governing Law & Dispute Resolution
      </h2>

      <h3 className="terms-card-title">17.1 Governing Law</h3>
      <p className="terms-text">
        These Terms shall be governed by and construed in accordance with the laws of the United States
        of America, without regard to conflict of law principles. To the extent applicable, the laws of
        the state in which TOL's primary operator resides shall apply.
      </p>

      <h3 className="terms-card-title">17.2 Informal Resolution</h3>
      <p className="terms-text">
        Before filing any formal legal claim, you agree to attempt to resolve any dispute informally
        by contacting us at{' '}
        <a href="mailto:support@theorbitalledger.com" className="terms-internal-link">
          support@theorbitalledger.com
        </a>.
        We will attempt to resolve the dispute in good faith within 30 days.
      </p>

      <h3 className="terms-card-title">17.3 Binding Arbitration</h3>
      <p className="terms-text">
        If informal resolution is unsuccessful, any dispute, claim, or controversy arising out of or
        relating to these Terms or the Service shall be resolved by binding arbitration administered
        in accordance with the rules of the American Arbitration Association (AAA). Arbitration shall
        be conducted on an individual basis; class action claims are not permitted.
      </p>

      <h3 className="terms-card-title">17.4 Class Action Waiver</h3>
      <p className="terms-text">
        <span className="terms-bold">
          YOU AGREE THAT ANY DISPUTE RESOLUTION PROCEEDINGS WILL BE CONDUCTED ONLY ON AN INDIVIDUAL
          BASIS AND NOT IN A CLASS, CONSOLIDATED, OR REPRESENTATIVE ACTION.
        </span>{' '}
        If for any reason a claim proceeds in court rather than in arbitration, you waive any right to
        a jury trial.
      </p>

      <h3 className="terms-card-title">17.5 Exceptions</h3>
      <p className="terms-text">
        Notwithstanding the above, either party may seek injunctive or other equitable relief in any
        court of competent jurisdiction to prevent the actual or threatened infringement,
        misappropriation, or violation of intellectual property rights.
      </p>
      <BackToTop />
    </section>

    {/* ═══════════════════════════════════════════════
        SECTION 18 — CIG / RSI Disclaimer
    ═══════════════════════════════════════════════ */}
    <section id="cig-disclaimer" className="terms-section">
      <h2 className="terms-section-heading">
        <span className="terms-section-number">18.</span> CIG / RSI Disclaimer
      </h2>
      <div className="terms-cig-disclaimer">
        <p>
          <span className="terms-bold">
            The Orbital Ledger is a fan-made community tool and is NOT affiliated with, endorsed by,
            or sponsored by Cloud Imperium Games Corporation ("CIG"), Roberts Space Industries ("RSI"),
            or any of their subsidiaries or affiliates.
          </span>
        </p>
        <p>
          Star Citizen{'\u00AE'}, Squadron 42{'\u00AE'}, Roberts Space Industries{'\u00AE'}, and
          Cloud Imperium Games{'\u00AE'} are registered trademarks of Cloud Imperium Games Corporation.
          All in-game items, ships, locations, and related content are the property of CIG.
        </p>
        <p>
          This tool is created under CIG's Fan Site Kit guidelines and is intended solely as a
          community resource to enhance the Star Citizen gameplay experience. Any use of Star Citizen
          game assets is done in accordance with CIG's content creation guidelines.
        </p>
        <p>
          CIG is not responsible for the content, functionality, accuracy, or availability of The Orbital
          Ledger. Any issues with TOL should be directed to us, not to CIG or RSI.
        </p>
        <p>
          For CIG's official website and terms, visit{' '}
          <a href="https://robertsspaceindustries.com" className="terms-internal-link" target="_blank" rel="noopener noreferrer">
            robertsspaceindustries.com
          </a>.
        </p>
      </div>
      <BackToTop />
    </section>

    {/* ═══════════════════════════════════════════════
        SECTION 19 — Contact Information
    ═══════════════════════════════════════════════ */}
    <section id="contact" className="terms-section">
      <h2 className="terms-section-heading">
        <span className="terms-section-number">19.</span> Contact Information
      </h2>
      <p className="terms-text">
        If you have any questions, concerns, or requests regarding these Terms of Service, your account,
        or the Service in general, please contact us:
      </p>
      <div className="terms-contact">
        <p className="terms-text" style={{ marginBottom: 4 }}>
          <span className="terms-bold">The Orbital Ledger</span>
        </p>
        <p className="terms-text" style={{ marginBottom: 12 }}>
          <span className="terms-bold">Email:</span>{' '}
          <a href="mailto:support@theorbitalledger.com" className="terms-contact-email">
            support@theorbitalledger.com
          </a>
        </p>
        <p className="terms-text" style={{ marginBottom: 4 }}>
          <span className="terms-bold">Website:</span>{' '}
          <a href="https://theorbitalledger.com" className="terms-internal-link" target="_blank" rel="noopener noreferrer">
            theorbitalledger.com
          </a>
        </p>
        <p className="terms-text terms-emphasis" style={{ marginBottom: 0, marginTop: 16, fontSize: '0.85rem' }}>
          We aim to respond to all inquiries within 48 hours during business days.
        </p>
      </div>
      <BackToTop />
    </section>

    {/* ── Footer ── */}
    <footer className="terms-footer-note">
      <p>
        By using The Orbital Ledger, you acknowledge that you have read, understood, and agreed to these
        Terms of Service in their entirety.
      </p>
      <p style={{ marginTop: 8 }}>
        &copy; {new Date().getFullYear()} The Orbital Ledger. All rights reserved.
        Not affiliated with Cloud Imperium Games or Roberts Space Industries.
      </p>
    </footer>
  </div>
);

export default TermsPage;
